import { computed, inject, Injectable, Signal } from '@angular/core'
import {
  AppointmentModel,
  AppointmentSelectOption,
  Clinic,
  EligibilityBenefits,
  Estimate,
  EstimateBalance,
  EstimateBalancePaymentStatus,
  EstimateBalanceWorkflowStatus,
  EstimateData,
  Facility,
  Patient,
  PatientCharityCareStatus,
  PatientInsurance,
} from '@mmx/shared'

import { type ChargeGroupingLike, hasPricerTrace } from '../../pricer-trace-message'
import {
  BalanceSummaryCapabilities,
  BalanceSummaryData,
} from '../balance-summary.types'
import { distinctNonEmptyStrings, resolveChargeAmount, resolveChargeQuantity } from '../balance-utils'
import { EstimateBalanceCalculationService } from './estimate-balance-calculation.service'
import { EstimateBalanceLoadingState, EstimateBalanceStateService } from './estimate-balance-state.service'

const CPT_PREVIEW_LIMIT = 4

export type ModifySessionPhase = 'finalized' | 'modifyPreCalc' | 'modifyPostCalc'

export interface ModifyDiffViewModel {
  newSet: ReadonlySet<number>
  renumberedFrom: ReadonlyMap<number, number>
  removed: ReadonlyArray<{ visitNumber: number; appointmentId: string | null }>
}

export interface ModifySessionViewModel {
  phase: ModifySessionPhase
  isModifying: boolean
  canModify: boolean
  canCalculate: boolean
  canFinalize: boolean
  canVoid: boolean
  diff: ModifyDiffViewModel | null
}

export interface BalanceStatusViewModel {
  workflowKey: string
  paymentKey: string
  workflowStatus: EstimateBalanceWorkflowStatus
  paymentStatus: EstimateBalancePaymentStatus
}

export interface MultiVisitReviewRow {
  visitNumber: number
  /** Linked appointment id, or `null` for unscheduled visits. Used to match pricer trace keys. */
  appointmentId: string | null
  appointmentStartAt: Date | null
  modeKey: 'scheduled' | 'unscheduled'
  patientResp: number
  insuranceResp: number
  total: number
  /** True when a PB pricer trace exists for this visit. Drives the PB trace button. */
  hasPbTrace: boolean
  /** True when an HB pricer trace exists for this visit. Drives the HB trace button. */
  hasHbTrace: boolean
  /** Distinct CPT codes in first-seen order. Empty for pre-contract rows that lack `cptCodes`. */
  cptCodes: string[]
  /** Pre-joined `cptCodes` — computed once in the view-model so the template doesn't re-join per CD pass. */
  cptCodesJoined: string
  cptCodesPreview: string
  additionalCptCodeCount: number
  charges: MultiVisitReviewCharge[]
  /** Per-visit CPT discount, stored negative. 0 when no discount applied → discount line hidden. */
  cptDiscountApplied: number
  /** Per-visit charity care discount amount. 0 when not applied → line hidden. */
  charityCareDiscountApplied: number
  diff: 'new' | 'renumbered' | null
  renumberedFrom: number | null
}

export interface MultiVisitReviewCharge {
  /** Empty for facility lines billed under a revenue code alone. */
  cptCode: string
  description: string
  quantity: number
  /** Statement amount; `null` when the charge carries no usable amount. */
  price: number | null
}

export interface MultiVisitReviewTotals {
  patientResp: number
  insuranceResp: number
  total: number
}

export interface MultiVisitReviewViewModel {
  rows: MultiVisitReviewRow[]
  totals: MultiVisitReviewTotals
  workflowStatus: EstimateBalanceWorkflowStatus | null
  canFinalize: boolean
  isEmpty: boolean
}

export const EMPTY_MULTI_VISIT_REVIEW: MultiVisitReviewViewModel = {
  rows: [],
  totals: { patientResp: 0, insuranceResp: 0, total: 0 },
  workflowStatus: null,
  canFinalize: false,
  isEmpty: true,
}

export interface EstimateBalanceViewModel {
  loadingState: EstimateBalanceLoadingState
  error: string | null
  balance: EstimateBalance | null
  estimate: EstimateData | null
  appointmentOptions: AppointmentSelectOption[]
  /**
   * Earliest-dated currently-selected appointment - what the Insurance Card's "This visit" tab
   * should reflect. Falls back to the first selected appointment when none of them are dated
   * (ORDER-type), so the card's add/edit/delete/pull-eligibility keep an appointment to target.
   */
  visitAppointment: AppointmentModel | null
  /**
   * Every currently-selected appointment (unordered, unfiltered by startAt) - lets the
   * Insurance Card widen "This Visit" to the union of all selected visits' accounts for
   * Course of Care estimates, instead of just visitAppointment's single earliest visit.
   */
  visitAppointments: AppointmentModel[]
  primaryInsurance: PatientInsurance | null
  insuranceSelectionResolved: boolean
  secondaryInsurance: PatientInsurance | null
  primaryInsuranceOptions: PatientInsurance[]
  secondaryInsuranceOptions: PatientInsurance[]
  hasSecondaryInsurance: boolean
  primaryBenefits: EligibilityBenefits | null
  secondaryBenefits: EligibilityBenefits | null
  primaryBenefitsLoading: boolean
  secondaryBenefitsLoading: boolean
  needsPricing: boolean
  dirty: boolean
  claimSearchEnabled: boolean
  claimSearchPrefillQuery: string | null
  status: BalanceStatusViewModel | null
  patientType: Estimate.PATIENT_TYPE
  canSave: boolean
  canDelete: boolean
  showInputs: boolean
  charityCareVisible: boolean
  charityCareTimezone: string | null
  patientCharityCareStatus: PatientCharityCareStatus | null
  summaryData: BalanceSummaryData
  summaryCapabilities: BalanceSummaryCapabilities
  cocMode: boolean
  unscheduled: boolean
  hasEstimateLevelMcsTrace: boolean
  multiVisitReview: MultiVisitReviewViewModel
  modifySession: ModifySessionViewModel
}

export interface ViewModelInputs {
  clinic: Signal<Clinic | null>
  facility: Signal<Facility | null>
  patient: Signal<Patient | null>
  charityCareStatus: Signal<PatientCharityCareStatus | undefined>
  claimSearchEnabled: Signal<boolean>
  claimSearchPrefillQuery: Signal<string | null>
  hasLocalMcsTrace: Signal<boolean>
}

const WORKFLOW_KEY: Record<EstimateBalanceWorkflowStatus, string> = {
  [EstimateBalanceWorkflowStatus.DRAFT]: 'balance.workflow-status.draft',
  [EstimateBalanceWorkflowStatus.CONFIRMED]: 'balance.workflow-status.confirmed',
  [EstimateBalanceWorkflowStatus.NA]: 'balance.workflow-status.na',
  [EstimateBalanceWorkflowStatus.NOT_READY]: 'balance.workflow-status.not-ready',
  [EstimateBalanceWorkflowStatus.NOT_STARTED]: 'balance.workflow-status.not-started',
}

const PAYMENT_KEY: Record<EstimateBalancePaymentStatus, string> = {
  [EstimateBalancePaymentStatus.UNPAID]: 'balance.payment-status.unpaid',
  [EstimateBalancePaymentStatus.PARTIAL]: 'balance.payment-status.partial',
  [EstimateBalancePaymentStatus.PAID]: 'balance.payment-status.paid',
  [EstimateBalancePaymentStatus.OVERPAID]: 'balance.payment-status.overpaid',
  [EstimateBalancePaymentStatus.REFUNDED]: 'balance.payment-status.refunded',
}

@Injectable()
export class EstimateBalancePresenterService {
  private readonly state = inject(EstimateBalanceStateService)
  private readonly calcService = inject(EstimateBalanceCalculationService)

  canModify(estimate: EstimateData | null, balance: EstimateBalance | null): boolean {
    return (
      balance?.workflowStatus === EstimateBalanceWorkflowStatus.CONFIRMED
      && estimate?.modifySnapshot == null
    )
  }

  formatStatus(balance: EstimateBalance): BalanceStatusViewModel {
    return {
      workflowKey: WORKFLOW_KEY[balance.workflowStatus] ?? 'balance.workflow-status.unknown',
      paymentKey: PAYMENT_KEY[balance.paymentStatus] ?? 'balance.payment-status.unknown',
      workflowStatus: balance.workflowStatus,
      paymentStatus: balance.paymentStatus,
    }
  }

  // null balance ≠ blocker — the EstimateBalance row is created at finalize from estimate.output,
  // so a saved estimate with pricer visits is finalize-ready even before any balance exists.
  canFinalize(
    estimate: EstimateData | null,
    balance: EstimateBalance | null,
    dirty: boolean,
  ): boolean {
    if (estimate?.id == null || dirty) {
      return false
    }
    if ((estimate.output?.visits?.length ?? 0) === 0) {
      return false
    }
    return balance == null || balance.workflowStatus === EstimateBalanceWorkflowStatus.DRAFT
  }

  canRevert(balance: EstimateBalance): boolean {
    return balance.workflowStatus === EstimateBalanceWorkflowStatus.CONFIRMED
  }

  // Same gate as the legacy `estimateFinalized()` predicate on <mmx-estimate-send-invoice>.
  canSend(balance: EstimateBalance | null): boolean {
    return balance?.workflowStatus === EstimateBalanceWorkflowStatus.CONFIRMED
  }

  // Print/Generate tracks Send — same snapshot lineage.
  canPrintLetter(balance: EstimateBalance | null): boolean {
    return balance?.workflowStatus === EstimateBalanceWorkflowStatus.CONFIRMED
  }

  canSave(estimate: EstimateData | null, dirty: boolean): boolean {
    return estimate != null && dirty
  }

  canDelete(estimate: EstimateData | null, balance: EstimateBalance | null): boolean {
    if (estimate?.id == null) {
      return false
    }
    return balance == null || !balance.paidAmount
  }

  // Inputs section is editable while the balance has never left DRAFT (or doesn't exist yet).
  showInputs(estimate: EstimateData | null, balance: EstimateBalance | null): boolean {
    if (estimate == null) {
      return false
    }
    return balance == null || balance.workflowStatus === EstimateBalanceWorkflowStatus.DRAFT
  }

  // One computed over state + caller's signals. Returns a fresh VM each recompute; templates
  // bind to the signal read, so OnPush tracks it through identity changes.
  viewModel(inputs: ViewModelInputs): Signal<EstimateBalanceViewModel> {
    return computed(() => this.buildViewModel({
      loadingState: this.state.loadingState(),
      error: this.state.error(),
      balance: this.state.balance(),
      estimate: this.state.estimate(),
      appointmentOptions: this.state.appointmentOptions(),
      primaryInsurance: this.state.primaryInsurance(),
      insuranceSelectionResolved: this.state.insuranceSelectionResolved(),
      secondaryInsurance: this.state.secondaryInsurance(),
      primaryInsuranceOptions: this.state.primaryInsuranceOptions(),
      secondaryInsuranceOptions: this.state.secondaryInsuranceOptions(),
      hasSecondaryInsurance: this.state.hasSecondaryInsurance(),
      primaryBenefits: this.state.primaryBenefits(),
      secondaryBenefits: this.state.secondaryBenefits(),
      primaryBenefitsLoading: this.state.primaryBenefitsLoading(),
      secondaryBenefitsLoading: this.state.secondaryBenefitsLoading(),
      needsPricing: this.state.needsPricing(),
      dirty: this.state.dirty(),
      claimSearchEnabled: inputs.claimSearchEnabled(),
      claimSearchPrefillQuery: inputs.claimSearchPrefillQuery(),
      hasLocalMcsTrace: inputs.hasLocalMcsTrace(),
      clinic: inputs.clinic(),
      facility: inputs.facility(),
      patient: inputs.patient(),
      charityCareStatus: inputs.charityCareStatus(),
      cocMode: this.state.cocMode(),
      unscheduled: this.state.unscheduled(),
      appointments: this.state.appointments(),
      modifying: this.state.modifying(),
      selectedAppointmentIds: this.state.selectedAppointmentIds(),
      selectedAppointments: this.state.selectedAppointments(),
    }))
  }

  private buildViewModel(snapshot: {
    loadingState: EstimateBalanceLoadingState
    error: string | null
    balance: EstimateBalance | null
    estimate: EstimateData | null
    appointmentOptions: AppointmentSelectOption[]
    primaryInsurance: PatientInsurance | null
    insuranceSelectionResolved: boolean
    secondaryInsurance: PatientInsurance | null
    primaryInsuranceOptions: PatientInsurance[]
    secondaryInsuranceOptions: PatientInsurance[]
    hasSecondaryInsurance: boolean
    primaryBenefits: EligibilityBenefits | null
    secondaryBenefits: EligibilityBenefits | null
    primaryBenefitsLoading: boolean
    secondaryBenefitsLoading: boolean
    needsPricing: boolean
    dirty: boolean
    claimSearchEnabled: boolean
    claimSearchPrefillQuery: string | null
    hasLocalMcsTrace: boolean
    clinic: Clinic | null
    facility: Facility | null
    patient: Patient | null
    charityCareStatus: PatientCharityCareStatus | undefined
    cocMode: boolean
    unscheduled: boolean
    appointments: AppointmentModel[]
    modifying: boolean
    selectedAppointmentIds: string[]
    selectedAppointments: AppointmentModel[]
  }): EstimateBalanceViewModel {
    const { balance, estimate, clinic, facility, patient } = snapshot
    const modifySession = this.buildModifySession(estimate, balance, snapshot)
    // One definition of "selected" for both fields below. state.selectedAppointments drops
    // anything without a startAt (ORDER-type appointments never get one, see appointment.ts),
    // so it can't be the only source: an ORDER-only selection would leave visitAppointment
    // null and strip appointmentId/accountId from the card's add/edit/delete/pull-eligibility.
    const selectedAppointments = snapshot.appointments.filter(a => snapshot.selectedAppointmentIds.includes(a.id))
    const status = balance == null ? null : this.formatStatus(balance)
    const canFinalize = this.canFinalize(estimate, balance, snapshot.needsPricing)
    const canRevert = balance == null ? false : this.canRevert(balance)
    const summaryCapabilities: BalanceSummaryCapabilities = {
      canSave: this.canSave(estimate, snapshot.dirty),
      canFinalize,
      canRevert,
      canDelete: this.canDelete(estimate, balance),
      canViewHistory: !!estimate?.id,
      canSend: this.canSend(balance),
      canSendEmail: this.canSend(balance) && patient?.canEmail === true,
      canSendSms: this.canSend(balance) && patient?.canText === true,
      canSendDocument: this.canSend(balance) && patient?.canEmail === true,
      canPrintLetter: this.canPrintLetter(balance),
    }
    return {
      loadingState: snapshot.loadingState,
      error: snapshot.error,
      balance,
      estimate,
      appointmentOptions: snapshot.appointmentOptions,
      visitAppointment: snapshot.selectedAppointments[0] ?? selectedAppointments[0] ?? null,
      visitAppointments: selectedAppointments,
      primaryInsurance: snapshot.primaryInsurance,
      insuranceSelectionResolved: snapshot.insuranceSelectionResolved,
      secondaryInsurance: snapshot.secondaryInsurance,
      primaryInsuranceOptions: snapshot.primaryInsuranceOptions,
      secondaryInsuranceOptions: snapshot.secondaryInsuranceOptions,
      hasSecondaryInsurance: snapshot.hasSecondaryInsurance,
      primaryBenefits: snapshot.primaryBenefits,
      secondaryBenefits: snapshot.secondaryBenefits,
      primaryBenefitsLoading: snapshot.primaryBenefitsLoading,
      secondaryBenefitsLoading: snapshot.secondaryBenefitsLoading,
      needsPricing: snapshot.needsPricing,
      dirty: snapshot.dirty,
      claimSearchEnabled: snapshot.claimSearchEnabled,
      claimSearchPrefillQuery: snapshot.claimSearchPrefillQuery,
      status,
      patientType: estimate?.patientType ?? Estimate.PATIENT_TYPE.OutPatient,
      canSave: summaryCapabilities.canSave,
      canDelete: summaryCapabilities.canDelete,
      showInputs: this.showInputs(estimate, balance),
      charityCareVisible: clinic?.modules?.charityCare === true && patient != null,
      charityCareTimezone: facility?.timezone ?? clinic?.settings?.defaultTimezone ?? null,
      patientCharityCareStatus: snapshot.charityCareStatus ?? patient?.patientCharityCareStatus ?? null,
      summaryData: {
        balance,
        estimate,
        latestSnapshot: balance?.letterSnapshots?.[balance.letterSnapshots.length - 1] ?? null,
      },
      summaryCapabilities,
      cocMode: snapshot.cocMode,
      unscheduled: snapshot.unscheduled,
      hasEstimateLevelMcsTrace: (estimate?.modelClaimSearchTraces?.length ?? 0) > 0 || snapshot.hasLocalMcsTrace,
      multiVisitReview: this.buildMultiVisitReview(
        estimate,
        balance,
        snapshot.appointments,
        snapshot.needsPricing,
        modifySession.diff,
      ),
      modifySession,
    }
  }

  private buildModifySession(
    estimate: EstimateData | null,
    balance: EstimateBalance | null,
    snapshot: {
      modifying: boolean
      dirty: boolean
      needsPricing: boolean
      unscheduled: boolean
      selectedAppointmentIds: string[]
    },
  ): ModifySessionViewModel {
    const isModifying = snapshot.modifying
    const phase: ModifySessionPhase = !isModifying
      ? 'finalized'
      : snapshot.needsPricing ? 'modifyPreCalc' : 'modifyPostCalc'
    const canCalculate = this.buildCanCalculate(estimate, snapshot)
    const subItemsCount = balance?.subItems?.length ?? 0
    const canFinalize = phase === 'modifyPostCalc' && !snapshot.needsPricing && subItemsCount > 0
    const canVoid = this.canDelete(estimate, balance)
    return {
      phase,
      isModifying,
      canModify: this.canModify(estimate, balance),
      canCalculate,
      canFinalize,
      canVoid,
      diff: this.buildModifyDiff(estimate, balance, phase),
    }
  }

  private buildCanCalculate(
    estimate: EstimateData | null,
    snapshot: { unscheduled: boolean; selectedAppointmentIds: string[] },
  ): boolean {
    const charges = estimate?.charges ?? []
    if (charges.length === 0) {
      return false
    }
    if (snapshot.unscheduled) {
      return this.calcService.hasUnscheduledPositiveVisitNumbers(charges)
    }
    return snapshot.selectedAppointmentIds.length > 0
  }

  private buildModifyDiff(
    estimate: EstimateData | null,
    balance: EstimateBalance | null,
    phase: ModifySessionPhase,
  ): ModifyDiffViewModel | null {
    if (phase !== 'modifyPostCalc' || estimate?.modifySnapshot == null || balance == null) {
      return null
    }
    const raw = this.calcService.computeModifyDiff(
      estimate.modifySnapshot.visits,
      estimate.output?.visits ?? [],
    )
    return {
      newSet: new Set(raw.new),
      renumberedFrom: new Map(raw.renumbered),
      removed: [...raw.removed],
    }
  }

  private buildRowDiffAnnotation(
    diff: ModifyDiffViewModel | null,
    visitNumber: number,
  ): Pick<MultiVisitReviewRow, 'diff' | 'renumberedFrom'> {
    if (diff == null) {
      return { diff: null, renumberedFrom: null }
    }
    if (diff.newSet.has(visitNumber)) {
      return { diff: 'new', renumberedFrom: null }
    }
    const renumberedFrom = diff.renumberedFrom.get(visitNumber)
    if (renumberedFrom != null) {
      return { diff: 'renumbered', renumberedFrom }
    }
    return { diff: null, renumberedFrom: null }
  }

  private buildMultiVisitReview(
    estimate: EstimateData | null,
    balance: EstimateBalance | null,
    appointments: AppointmentModel[],
    needsPricing: boolean,
    diff: ModifyDiffViewModel | null,
  ): MultiVisitReviewViewModel {
    const modeKey: 'scheduled' | 'unscheduled' = estimate?.mode === Estimate.MODE.SCHEDULED ? 'scheduled' : 'unscheduled'
    const status = balance?.workflowStatus ?? null
    const finalizableStatus = status !== EstimateBalanceWorkflowStatus.CONFIRMED
      && status !== EstimateBalanceWorkflowStatus.NA
      && status !== EstimateBalanceWorkflowStatus.NOT_READY
    const canFinalize = this.canFinalize(estimate, balance, needsPricing) && finalizableStatus

    if (balance != null) {
      const apptById = new Map<string, AppointmentModel>()
      for (const appt of appointments) {
        if (appt.id) {
          apptById.set(appt.id, appt)
        }
      }
      const rows: MultiVisitReviewRow[] = [...balance.subItems]
        .sort((a, b) => a.visitNumber - b.visitNumber)
        .map(item => {
          const appt = item.appointmentId ? apptById.get(item.appointmentId) ?? null : null
          const startAt = appt?.startAt ? appt.startAt.toDate() : null
          const cptCodes = item.cptCodes ?? []
          const grouping = { visitNumber: item.visitNumber, appointmentId: item.appointmentId ?? null }
          return {
            visitNumber: item.visitNumber,
            appointmentId: item.appointmentId ?? null,
            appointmentStartAt: startAt,
            modeKey,
            patientResp: item.patientResp,
            insuranceResp: item.insuranceResp,
            total: item.total,
            hasPbTrace: hasPricerTrace(estimate?.pbResponses, estimate?.pbRequests, grouping),
            hasHbTrace: hasPricerTrace(estimate?.hbResponses, estimate?.hbRequests, grouping),
            cptCodes,
            cptCodesJoined: cptCodes.join(', '),
            ...buildCptPreview(cptCodes),
            ...buildChargeBreakdown(estimate, grouping, needsPricing),
            cptDiscountApplied: item.cptDiscountApplied ?? 0,
            charityCareDiscountApplied: item.charityCareDiscountApplied ?? 0,
            ...this.buildRowDiffAnnotation(diff, item.visitNumber),
          }
        })
      return {
        rows,
        totals: {
          patientResp: balance.patientResponsibility,
          insuranceResp: balance.insuranceResponsibility,
          total: balance.totalAmount,
        },
        workflowStatus: status,
        canFinalize,
        isEmpty: false,
      }
    }

    const visits = estimate?.output?.visits ?? []
    if (visits.length > 0) {
      const rows: MultiVisitReviewRow[] = [...visits]
        .sort((a, b) => a.visitNumber - b.visitNumber)
        .map(visit => {
          const cptCodes = distinctCpts(visit.charges ?? [])
          const grouping = { visitNumber: visit.visitNumber, appointmentId: visit.appointmentId ?? null }
          return {
            visitNumber: visit.visitNumber,
            appointmentId: visit.appointmentId ?? null,
            appointmentStartAt: null,
            modeKey,
            patientResp: visit.patientsResponsibility,
            insuranceResp: visit.insuranceResponsibility + (visit.secondaryInsuranceResponsibility ?? 0),
            total: visit.total,
            hasPbTrace: hasPricerTrace(estimate?.pbResponses, estimate?.pbRequests, grouping),
            hasHbTrace: hasPricerTrace(estimate?.hbResponses, estimate?.hbRequests, grouping),
            cptCodes,
            cptCodesJoined: cptCodes.join(', '),
            ...buildCptPreview(cptCodes),
            ...buildChargeBreakdown(estimate, grouping, needsPricing),
            cptDiscountApplied: visit.cptDiscountApplied ?? 0,
            charityCareDiscountApplied: visit.charityCareDiscountApplied ?? 0,
            ...this.buildRowDiffAnnotation(diff, visit.visitNumber),
          }
        })
      const output = estimate?.output
      return {
        rows,
        totals: {
          patientResp: output?.patientsResponsibility ?? 0,
          insuranceResp: (output?.insuranceResponsibility ?? 0) + (output?.secondaryInsuranceResponsibility ?? 0),
          total: output?.total ?? 0,
        },
        workflowStatus: status,
        canFinalize,
        isEmpty: false,
      }
    }

    return { ...EMPTY_MULTI_VISIT_REVIEW, workflowStatus: status, canFinalize: false }
  }
}

function distinctCpts(charges: ReadonlyArray<{ cptCode?: string | null }>): string[] {
  return distinctNonEmptyStrings(charges.map(c => c.cptCode))
}

/**
 * One row per charge on the visit, priced the way the printed statement prices it, so the
 * breakdown and the statement a patient receives cannot disagree. Uncoded facility lines
 * (operating room, supplies) are kept: dropping them left the rows unable to reach the visit total.
 */
function buildChargeBreakdown(
  estimate: EstimateData | null,
  grouping: ChargeGroupingLike,
  needsPricing: boolean,
): Pick<MultiVisitReviewRow, 'charges'> {
  if (needsPricing) {
    return { charges: [] }
  }

  const charges = estimate?.output?.visits
    ?.find(visit => visit.visitNumber === grouping.visitNumber)
    ?.charges ?? []

  return {
    charges: charges.map(charge => ({
      cptCode: charge.cptCode?.trim() ?? '',
      // chargeCode keeps a description-less line identifiable, and searchable.
      description: charge.description?.trim() || charge.chargeCode?.trim() || '',
      quantity: resolveChargeQuantity(charge.quantity),
      price: resolveChargeAmount(charge),
    })),
  }
}

function buildCptPreview(cptCodes: string[]): Pick<MultiVisitReviewRow, 'cptCodesPreview' | 'additionalCptCodeCount'> {
  const visibleCptCodes = cptCodes.slice(0, CPT_PREVIEW_LIMIT)
  return {
    cptCodesPreview: visibleCptCodes.join(', '),
    additionalCptCodeCount: Math.max(cptCodes.length - visibleCptCodes.length, 0),
  }
}
